import { 
  processCampusMobileAuthResult,
  requestCampusMobileAuth
} from './campusMobileAuthApi';

// 본인확인 결과 타입
export interface VerificationResult {
  success: boolean;
  message: string;
  userData?: {
    id: number;
    name: string;
    phone: string;
    birth: string;
  };
}

// 본인인증 요청 데이터 타입
export interface AuthRequestData {
  name: string;
  birthYear: string;
  birthMonth: string;
  birthDay: string;
}

/**
 * 생년월일을 YYYY-MM-DD 형식으로 포맷팅
 */
export const formatBirthDate = (birthYear: string, birthMonth: string, birthDay: string): string => {
  if (!birthYear || !birthMonth || !birthDay) {
    return '';
  }
  
  const formattedMonth = birthMonth.padStart(2, '0');
  const formattedDay = birthDay.padStart(2, '0');
  
  return `${birthYear}-${formattedMonth}-${formattedDay}`;
};

/**
 * Mobile-OK 본인인증 팝업 창을 여는 함수 (드림시큐리티)
 */
export const openMobileOKAuthPopup = (authRequestData: any): Window | null => {
  try {
    console.log('Mobile-OK 인증 데이터 전체:', authRequestData);
    
    // Mobile-OK MOBILEOK.process() 함수 사용
    if (!(window as any).MOBILEOK) {
      throw new Error('Mobile-OK 스크립트가 로드되지 않았습니다. 페이지를 새로고침해주세요.');
    }

    // Mobile-OK에서 제공하는 요청 URL (백엔드에서 받은 데이터)
    let requestUrl = authRequestData.requestUrl || `${process.env.REACT_APP_CAMPUS_API_BASE_URL}/api/mobileok/request`;
    
    // name과 birth 파라미터를 URL에 추가
    if (authRequestData.name && authRequestData.birth) {
      const urlParams = new URLSearchParams();
      urlParams.append('name', authRequestData.name);
      urlParams.append('birth', authRequestData.birth);
      requestUrl += `?${urlParams.toString()}`;
    }
    
    console.log('Mobile-OK 요청 URL:', requestUrl);
    console.log('환경변수 확인:', process.env.REACT_APP_CAMPUS_API_BASE_URL);
    console.log('Mobile-OK 인증 시작...');
    
    // 팝업 창 참조를 저장하기 위한 변수
    let popupWindow: Window | null = null;
    
    // 전역 콜백 함수 정의
    (window as any).mobileOKResult = function(result: string) {
      console.log('Mobile-OK 인증 결과:', result);
      try {
        const parsedResult = JSON.parse(result);
        console.log('파싱된 결과:', parsedResult);
        
        // CampusPhoneVerification 컴포넌트의 콜백들도 호출
        const callbackNames = [
          'mobileOKCallback', 
          'mobileAuthResult',
          'receiveAuthResult',
          'niceAuthResult',
          'checkplusResult',
          'mobileOKAuthResult',
          'authResult',
          'mobileOKComplete',
          'authComplete',
          'mobileAuthCallback',
          'mobileOK',
          'authCallback',
          'verificationResult',
          'verificationComplete'
        ];
        
        // 등록된 다른 콜백들도 호출
        callbackNames.forEach(name => {
          if ((window as any)[name] && typeof (window as any)[name] === 'function') {
            console.log(`${name} 콜백 호출 시도`);
            try {
              (window as any)[name](parsedResult);
            } catch (error) {
              console.error(`${name} 콜백 호출 에러:`, error);
            }
          }
        });
        
      } catch (error) {
        console.error('결과 파싱 오류:', error);
        console.log('원본 결과:', result);
      }
    };
    
    // Mobile-OK의 기본 팝업 창 설정을 가로채서 참조를 얻기 위한 임시 방법
    const originalWindowOpen = window.open;
    
    // window.open 메서드를 일시적으로 오버라이드
    window.open = function(url?: string | URL, target?: string, features?: string) {
      popupWindow = originalWindowOpen.call(this, url, target, features);
      console.log('Mobile-OK 팝업 창이 열렸습니다:', popupWindow);
      
      // 원본 메서드 복원
      window.open = originalWindowOpen;
      
      return popupWindow;
    };
    
    // Mobile-OK 표준창 호출
    // MOBILEOK.process(요청URL, 디바이스타입, 콜백함수)
    (window as any).MOBILEOK.process(
      requestUrl,
      'WB', // PC웹: 'WB', 모바일웹: 'MB'
      'mobileOKResult' // 전역 콜백 함수명
    );
    
    // window.open이 호출되지 않았다면 복원
    setTimeout(() => {
      if (window.open !== originalWindowOpen) {
        window.open = originalWindowOpen;
      }
    }, 3000);
    
    console.log('Mobile-OK 본인인증 창 호출 완료.');
    
    return popupWindow;
    
  } catch (error) {
    console.error('Mobile-OK 팝업 창 열기 실패:', error);
    throw error;
  }
};
export const startPhoneVerification = async (data: AuthRequestData): Promise<Window | null> => {
  // 입력값 검증
  if (!data.name.trim()) {
    throw new Error('이름을 입력해주세요.');
  }

  const birthDate = formatBirthDate(data.birthYear, data.birthMonth, data.birthDay);
  if (!birthDate) {
    throw new Error('생년월일을 선택해주세요.');
  }

  try {
    console.log('Mobile-OK 본인인증 시작');
    
    // Mobile-OK는 MOBILEOK.process() 함수로 직접 호출
    // 백엔드 API URL을 전달하여 Mobile-OK가 직접 요청하도록 함
    const authData = {
      requestUrl: `${process.env.REACT_APP_CAMPUS_API_BASE_URL}/api/mobileok/request`,
      name: data.name.trim(),
      birth: birthDate
    };

    console.log('Mobile-OK 인증 데이터:', authData);
    
    // Mobile-OK 본인인증 팝업 열기
    const popup = openMobileOKAuthPopup(authData);
    
    console.log('Mobile-OK 본인인증 창이 열렸습니다. 사용자가 인증을 완료하면 자동으로 처리됩니다.');
    
    return popup;
    
  } catch (error: any) {
    console.error('Mobile-OK 본인인증 시작 중 오류:', error);
    
    let errorMsg = 'Mobile-OK 본인인증 요청 중 오류가 발생했습니다.';
    
    if (error.message?.includes('Mobile-OK 스크립트')) {
      errorMsg = 'Mobile-OK 스크립트 로딩에 실패했습니다. 페이지를 새로고침해주세요.';
    } else if (error.message?.includes('팝업 차단')) {
      errorMsg = '팝업 차단을 해제하고 다시 시도해주세요.';
    }
    
    throw new Error(errorMsg);
  }
};

/**
 * 본인인증 결과 처리
 */
export const processAuthResult = async (
  encryptMOKKeyToken: string,
  expectedName: string,
  expectedBirth: string
): Promise<VerificationResult> => {
  try {
    // 먼저 세션에 사용자 정보 저장
    console.log('세션에 사용자 정보 저장:', { name: expectedName, birth: expectedBirth });
    try {
      await requestCampusMobileAuth({
        name: expectedName,
        birth: expectedBirth
      });
    } catch (sessionError) {
      console.log('세션 저장 시도했지만 실패 (무시하고 계속):', sessionError);
    }

    const authResult = await processCampusMobileAuthResult({
      encryptMOKKeyToken
    });

    if (authResult.code === 200) {
      // 입력한 정보와 인증된 정보 검증
      const authData = authResult.data;
      
      if (expectedName.trim() !== authData.name.trim()) {
        return {
          success: false,
          message: `입력한 이름(${expectedName})과 인증된 이름(${authData.name})이 일치하지 않습니다.`
        };
      }

      if (expectedBirth !== authData.birth) {
        return {
          success: false,
          message: `입력한 생년월일(${expectedBirth})과 인증된 생년월일(${authData.birth})이 일치하지 않습니다.`
        };
      }

      // 성공 - Person 생성되고 id 반환됨
      return {
        success: true,
        message: '본인확인이 완료되었습니다.',
        userData: authData
      };
    } else {
      return {
        success: false,
        message: authResult.message || '본인확인에 실패했습니다.'
      };
    }
  } catch (error: any) {
    console.error('인증 결과 처리 중 오류:', error);
    
    let errorMsg = '본인확인 처리 중 오류가 발생했습니다.';
    
    if (error.response?.status === 400) {
      console.log('본인인증 400 에러 (정상 처리)');
      errorMsg = error.response.data?.message || '입력하신 정보를 다시 확인해주세요.';
    } else if (error.response?.data?.message) {
      errorMsg = error.response.data.message;
    } else if (error.code === 'ECONNABORTED') {
      errorMsg = '요청 시간이 초과되었습니다. 다시 시도해주세요.';
    } else if (error.message?.includes('Network Error')) {
      errorMsg = '네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.';
    }
    
    return {
      success: false,
      message: errorMsg
    };
  }
};
